import { useCallback, useState } from 'react';
import type { ResultFilters } from '../types/api';

/** Teto do contrato: `createExportSchema` aceita no máximo 5000 ids. */
export const MAX_SELECTED_RESULTS = 5000;

export interface ResultSelection {
  selectedIds: string[];
  isSelected: (id: string) => boolean;
  toggle: (id: string) => void;
  toggleAllLoaded: (ids: string[]) => void;
  clear: () => void;
  count: number;
  exceedsLimit: boolean;
}

/** Assinatura do recorte: muda quando qualquer filtro muda. */
function filterSignature(filters: ResultFilters): string {
  return [filters.platform, filters.type, filters.author, filters.keyword, filters.sort]
    .map((value) => value ?? '')
    .join('|');
}

/**
 * Seleção de resultados para exportar (SPEC-023 §10).
 *
 * Duas decisões que a assinatura não conta:
 *
 * 1. A seleção **não vai para a URL**, ao contrário dos filtros (RF-F12). O
 *    contrato aceita 5000 ids; na query string isso passa de 180 kB e quebra
 *    proxy no caminho. O preço é perder a seleção ao recarregar.
 * 2. Trocar filtro **limpa** a seleção. O backend aplica `selectedIds` depois
 *    do filtro, então manter marcado um item que o novo filtro não devolve
 *    produziria arquivo menor que a contagem exibida, sem explicação possível.
 */
export function useResultSelection(filters: ResultFilters): ResultSelection {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [signature, setSignature] = useState(() => filterSignature(filters));

  // Ajuste durante a renderização em vez de `useEffect`: assim nenhum render
  // chega a acontecer com a seleção antiga sobre o recorte novo.
  const current = filterSignature(filters);
  if (current !== signature) {
    setSignature(current);
    setSelectedIds([]);
  }

  const toggle = useCallback((id: string): void => {
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((item) => item !== id) : [...ids, id]));
  }, []);

  /**
   * Marca ou desmarca os resultados já carregados — não existe, no cliente, o
   * conjunto inteiro: a lista é paginada por cursor. Para exportar tudo o que o
   * filtro devolve, o caminho é não selecionar nada.
   */
  const toggleAllLoaded = useCallback((ids: string[]): void => {
    setSelectedIds((current) => {
      const allSelected = ids.length > 0 && ids.every((id) => current.includes(id));
      if (allSelected) return current.filter((id) => !ids.includes(id));

      const missing = ids.filter((id) => !current.includes(id));
      return [...current, ...missing];
    });
  }, []);

  const clear = useCallback((): void => setSelectedIds([]), []);

  return {
    selectedIds,
    isSelected: (id) => selectedIds.includes(id),
    toggle,
    toggleAllLoaded,
    clear,
    count: selectedIds.length,
    exceedsLimit: selectedIds.length > MAX_SELECTED_RESULTS,
  };
}
